// Pure, framework-free logic for the Payment Methods delete flow.
// Extracted so the "which card becomes default after a delete" decision can be
// unit-tested without rendering the component.

/** Is the given card id currently the default card in `cards`? */
export const isCardDefault = (cards = [], cardId) =>
  Boolean(cards.find((card) => card && card.id === cardId && card.is_default));

/**
 * Choose which remaining card should become the new default after `deletedId`
 * is removed. Preference: the first non-expired remaining card, otherwise the
 * first remaining card, otherwise null (no cards left).
 *
 * @param {Array} cards       full card list before deletion
 * @param {string} deletedId  id of the card being removed
 * @param {Function} isExpired (month, year) => boolean
 */
export const pickNextDefaultCardId = (cards = [], deletedId, isExpired) => {
  const remaining = cards.filter((card) => card && card.id !== deletedId);
  if (remaining.length === 0) return null;
  const check = typeof isExpired === "function" ? isExpired : () => false;
  const nonExpired = remaining.find(
    (card) => !check(card.expiry_month, card.expiry_year)
  );
  return (nonExpired || remaining[0]).id;
};

/**
 * Plan the outcome of deleting a card.
 *
 * Returns:
 *  - remaining:     the card list after removal
 *  - wasDefault:    whether the deleted card was the default
 *  - newDefaultId:  the id that must be persisted as the SERVER-side default
 *                   (only when the default was deleted AND cards remain),
 *                   otherwise null (nothing to tell the server).
 */
export const planCardDeletion = (cards = [], deletedId, isExpired) => {
  const remaining = cards.filter((card) => card && card.id !== deletedId);
  const wasDefault = isCardDefault(cards, deletedId);
  const newDefaultId =
    wasDefault && remaining.length > 0
      ? pickNextDefaultCardId(cards, deletedId, isExpired)
      : null;
  return { remaining, wasDefault, newDefaultId };
};
